import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Mui, useTheme } from "@leap4/frontend";
import { useCampaignScope } from "../../scope/CampaignScopeProvider";
import { usePredictionSeries } from "../reporting/chart/usePredictionSeries";
import { ProjectionChart } from "./ProjectionChart";
import { ProjectionEventCard } from "./ProjectionEventCard";
import { ProjectingPlaceholder } from "./ProjectingPlaceholder";
import { projectionWindow, summarizeEvent } from "./summarize";

const { Box, Paper, Typography, Alert, Button, useMediaQuery } = Mui;

const CHART_HEIGHT = 380;

const panelSx = { border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px", p: 3 };

/**
 * What the campaign is on course to deliver, rather than what it has delivered. Deliberately its own
 * page and not a dashboard tile: a projection carries a margin of error, and a number shown beside
 * settled figures gets read as one of them.
 */
const Projection = () => {
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down("sm"));
    const { campaign, campaigns, status: scopeStatus, projectionEvents, configStatus, reloadConfig } = useCampaignScope();

    const events = useMemo(() => projectionEvents.map((event) => ({ key: event.event, label: event.display_label || event.event })), [projectionEvents]);
    const [seriesReload, setSeriesReload] = useState(0);
    const [selectedEvent, setSelectedEvent] = useState<string>("");

    const eventKeys = useMemo(() => events.map((event) => event.key), [events]);
    const { series, error } = usePredictionSeries(campaign, eventKeys, seriesReload);
    const win = projectionWindow();

    const labelFor = useMemo(() => new Map(events.map((event) => [event.key, event.label])), [events]);
    const summaries = useMemo(
        () => series.map((one) => summarizeEvent(one, labelFor.get(one.event) || one.event, win)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [series, labelFor, win.from, win.to],
    );
    const selectedSeries = series.find((one) => one.event === selectedEvent) ?? series[0];
    const seriesLoading = configStatus === "ready" && events.length > 0 && series.length === 0 && !error;

    const layout = summaries.length === 1 ? "single" : summaries.length <= 3 ? "full" : "compact";

    const header = (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1">
                Projection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Attributed conversions from {dayjs(win.from).format("MMM D")} to {dayjs(win.to).format("MMM D")}: counted, still settling, and forecast. Dates are UTC.
            </Typography>
        </Box>
    );

    if (scopeStatus === "ready" && campaigns.length === 0) {
        return (
            <Box>
                {header}
                <Paper sx={panelSx}>
                    <Typography variant="h6">No campaigns assigned to your account</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        Contact your account manager to get access.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box>
            {header}

            {configStatus === "error" && (
                <Alert severity="error" role="alert" sx={{ mb: 2.5 }} action={<Button color="inherit" size="small" onClick={reloadConfig}>Retry</Button>}>
                    Couldn't load this campaign's projection settings.
                </Alert>
            )}

            {configStatus === "ready" && events.length === 0 && (
                <Paper sx={panelSx}>
                    <Typography variant="h6">No projected events for {campaign}</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        Projections haven't been set up for this campaign yet. Your account manager can enable them. Counted conversions are always available in Reporting.
                    </Typography>
                </Paper>
            )}

            {error && (
                <Alert severity="error" role="alert" sx={{ mb: 2.5 }} action={<Button color="inherit" size="small" onClick={() => setSeriesReload((k) => k + 1)}>Retry</Button>}>
                    The projection service didn't respond ({error}). Counted conversions are still available in Reporting.
                </Alert>
            )}

            {(configStatus === "loading" || seriesLoading) && (
                <ProjectingPlaceholder cards={Math.max(1, Math.min(events.length, 3))} chartHeight={CHART_HEIGHT} />
            )}

            {summaries.length > 0 && (
                <>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 1.25,
                            mb: 2.5,
                            gridTemplateColumns: layout === "single" ? "1fr" : { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: `repeat(${Math.min(summaries.length, layout === "full" ? 3 : 4)}, minmax(0, 1fr))` },
                            alignItems: "start",
                        }}
                    >
                        {summaries.map((summary) => (
                            <ProjectionEventCard
                                key={summary.event}
                                summary={summary}
                                win={win}
                                layout={layout}
                                selectable={summaries.length > 1}
                                selected={summary.event === (selectedSeries?.event ?? "")}
                                onSelect={() => setSelectedEvent(summary.event)}
                            />
                        ))}
                    </Box>

                    {selectedSeries && (
                        <Paper sx={{ ...panelSx, p: 2.5 }}>
                            <Typography variant="subtitle2" component="h2" sx={{ color: "text.secondary", mb: 1.5 }}>
                                Daily {labelFor.get(selectedSeries.event) || selectedSeries.event}
                            </Typography>
                            <ProjectionChart
                                eventLabel={labelFor.get(selectedSeries.event) || selectedSeries.event}
                                points={selectedSeries.points}
                                from={win.from}
                                to={win.to}
                                height={CHART_HEIGHT}
                                compact={compact}
                            />
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );
};

export default Projection;
